import React from 'react';
import { SearchParamNames } from '../../models/enums';
import { SearchState } from './reducers/searchReducer';

const useBrowserSearchHistory = ({ searchTerms, page, courseTypes, topics, subTopics, sort }: SearchState) => {
    React.useEffect(
        function updateBrowserHistory() {
            /**
             * To avoid extra browser history entries on the search page, we should try to use the same param order everywhere:
             * 1: Query (if not empty)
             * 2: Page (if > 1)
             * 3: CourseTypes (if not empty)
             * 4: Topics (if not empty)
             * 5: SubTopics (if not empty)
             * 6: Sort (if not empty)
             */
            const newSearchParams = new URLSearchParams(window.location.search); // preserve other params, e.g. google analytics tracking
            for (const key in SearchParamNames) {
                newSearchParams.delete(SearchParamNames[key as keyof typeof SearchParamNames]);
            }
            ['courseType', 'filters', 'orderBy', 'pageNumber', 'pageSize', 'Sort'].forEach((legacyParamName) =>
                newSearchParams.delete(legacyParamName),
            );
            if (searchTerms) {
                newSearchParams.set(SearchParamNames.Query, searchTerms);
            }
            if (page > 1) {
                newSearchParams.set(SearchParamNames.Page, `${page}`);
            }
            courseTypes.forEach((courseType) => newSearchParams.append(SearchParamNames.CourseTypes, courseType));
            topics.forEach((topic) => newSearchParams.append(SearchParamNames.Topics, topic));
            subTopics.forEach((subTopic) => newSearchParams.append(SearchParamNames.SubTopics, subTopic));
            if (sort) {
                newSearchParams.set(SearchParamNames.Sort, sort);
            }

            const newTitle = [
                'Search Courses',
                searchTerms,
                page > 1 && `page ${page}`,
                [...courseTypes].join(', '),
                [...topics].join(', '),
                [...subTopics].join(', '),
                sort && `sort by ${sort}`,
            ]
                .filter(Boolean)
                .join(' | ');

            const url = new URL(window.location.href);
            url.search = newSearchParams.toString();
            if (window.location.search !== url.search) {
                window.history.pushState({}, newTitle, url.href);
            }

            document.title = newTitle;
        },
        [searchTerms, page, courseTypes, topics, subTopics, sort],
    );
};

export { useBrowserSearchHistory };
